import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView, useAnimation, type Variants } from "framer-motion";
import Navbar from "../../components/modules/navbar/Navbar";
// import { SIGNUP } from "routes/CONSTANTS";
import TESCOMMagazine from "../../components/magazine/Magazine";

// ↑ adjust to your actual Navbar path

// ═══════════════════════════════════════════════════════════════════════════
// TESCOM LANDING PAGE — v2
//
// This is a from-scratch UX pass, not a re-skin of the Ministry of Health
// page. The old version cloned MOH's section order 1:1 (20 near-identical
// card-grid sections in a row), which buried the things an actual visitor —
// mostly one of TESCOM's 11,000+ teaching staff — comes here to do: check a
// posting, register for CBT, check promotion eligibility, or find the
// e-learning portal.
//
// What changed structurally:
//   • Hero leads with task shortcuts, not just a headline + two buttons.
//   • A sticky "folder tab" quick-nav replaces endless blind scrolling.
//   • The three leadership spotlights (Governor / Chairman / Secretary)
//     collapse into one Leadership section.
//   • "Featured Programmes" and "Functions of the Commission" — which were
//     two near-duplicate "what we do" grids — merge into one toggle.
//   • Zones, Departments and the Organogram — three ways of describing the
//     same organisational structure — merge into one tabbed section.
//   • Awards and News — both "things that happened" — share one section.
//   • The two partner lists (agencies + development partners) merge.
//   • Net: ~20 flat sections → ~13, with real wayfinding between them.
//
// Visual identity: institutional green stays (it's the real Ogun State
// government colour, shared with MOH), but the accent moves off Tailwind's
// stock yellow-400 to a warmer "chalk gold", the background moves off flat
// grey to a warm paper tone, and headings move to a serif built for
// certificates and official documents. The one recurring signature motif is
// a small circular verification stamp — used only on things that were
// actually verified/awarded (milestones, awards, leadership credentials) —
// which ties back to what a Teaching Service Commission fundamentally is:
// a record-keeper of teachers' qualifications and careers.
//
// Anything wrapped in [brackets] or flagged "NOTE:" is a placeholder —
// TESCOM leadership names, quotes, contact details, and partner lists were
// not verifiable from here, so swap them for confirmed copy before publishing.
// ═══════════════════════════════════════════════════════════════════════════

const STRAPI_URL = (import.meta as any).env?.VITE_STRAPI_API_URL ?? "http://localhost:1337";
const USE_MOCK = true; // ← flip to false once /api/issues is live

interface MagazineIssue {
  id: number;
  title: string;
  publishedDate?: string;
  totalPages?: number;
  coverImageUrl?: string | null;
}

const MOCK_ISSUE: MagazineIssue = {
  id: 1,
  title: "Committed to Education Excellence",
  publishedDate: "2025-12-01",
  totalPages: 28,
  coverImageUrl: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=400&q=70"
};

async function mockDelay(ms = 400): Promise<void> {
  return await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function fetchLatestIssue(): Promise<MagazineIssue | null> {
  if (USE_MOCK) {
    await mockDelay();
    return MOCK_ISSUE;
  }
  const res = await fetch(`${STRAPI_URL}/api/issues`);
  if (!res.ok) return null;
  const json = await res.json();
  const list: MagazineIssue[] = Array.isArray(json) ? json : json?.data ?? [];
  if (!list.length) return null;
  return [...list].sort((a, b) => ((b.publishedDate ?? "") > (a.publishedDate ?? "") ? 1 : -1))[0];
}

// ─── Design tokens ───────────────────────────────────────────────────────
// green    #0a2e1a  institutional (shared with MOH — do not change)
// green-2  #14532d  secondary / gradients
// gold     #C9971C  accent — replaces stock yellow-400
// gold-2   #E8C468  gold hover / light fill
// paper    #FAF7EF  warm background, replaces flat grey
// ink      #1C2320  primary text on paper
// stamp    #A3402C  signature accent — used ONLY on verified/awarded items

const FONT_IMPORTS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
  .tsc-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .tsc-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-feature-settings: "tnum"; }
  @keyframes tsc-ticker { from { transform: translateX(0) } to { transform: translateX(-50%) } }
  @media (prefers-reduced-motion: reduce) {
    .tsc-ticker-track { animation: none !important; }
  }
`;

// ─── Animation variants ─────────────────────────────────────────────────

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }
  })
};

const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({ opacity: 1, transition: { duration: 0.45, delay: i * 0.06 } })
};

const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -26 },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }
  })
};

function ScrollReveal({
  children,
  className,
  variants = fadeUp,
  custom = 0
}: {
  children: ReactNode;
  className?: string;
  variants?: Variants;
  custom?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const controls = useAnimation();
  useEffect(() => {
    if (inView) void controls.start("visible");
  }, [inView, controls]);
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variants}
      custom={custom}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────

/** The one recurring signature motif: a small verified/stamped badge. Use
 * sparingly, only on things that are genuinely a verified fact or award —
 * never as decoration. */
function VerifiedStamp({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-[#A3402C]/50 text-[#A3402C] rotate-[-8deg] shrink-0 ${className}`}
      aria-hidden
    >
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

function SectionHeader({
  eyebrow,
  title,
  sub,
  light = false
}: {
  eyebrow: string;
  title: ReactNode;
  sub: string;
  light?: boolean;
}) {
  return (
    <div className="mb-10 max-w-2xl">
      <ScrollReveal>
        <p
          className={`tsc-mono text-[11px] tracking-[0.18em] uppercase mb-3 ${
            light ? "text-[#E8C468]" : "text-[#7a5b12]"
          }`}
        >
          {eyebrow}
        </p>
        <h2
          className={`tsc-display text-3xl md:text-[2.6rem] leading-[1.08] font-semibold mb-3 ${
            light ? "text-white" : "text-[#14201a]"
          }`}
        >
          {title}
        </h2>
        <p className={`text-[15px] leading-relaxed ${light ? "text-white/70" : "text-[#5b6560]"}`}>
          {sub}
        </p>
      </ScrollReveal>
    </div>
  );
}

function FaqItem({
  q,
  a,
  isOpen,
  onToggle
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-[#e4ddc9] rounded-lg bg-white overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-semibold text-[#14201a]">{q}</span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-xl text-[#7a5b12] shrink-0 leading-none"
        >
          +
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-[13px] text-[#5b6560] leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Simple segmented toggle used to merge sections that used to be separate,
 * near-duplicate scroll sections (Programmes/Functions, Zones/Departments/Org). */
function SegmentedToggle({
  options,
  value,
  onChange,
  light = false
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  light?: boolean;
}) {
  return (
    <div
      role="tablist"
      className={`inline-flex flex-wrap gap-1 p-1 rounded-lg border ${
        light ? "bg-white/[0.06] border-white/15" : "bg-white border-[#e4ddc9]"
      }`}
    >
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt)}
            className={`px-4 py-2 rounded-md text-xs font-semibold tracking-wide transition-colors ${
              active
                ? "bg-[#0a2e1a] text-[#E8C468]"
                : light
                  ? "text-white/60 hover:text-white"
                  : "text-[#5b6560] hover:text-[#14201a]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── Content data ─────────────────────────────────────────────────────────

const TICKER_ITEMS = [
  "🎓 CBT Examination for Principals concludes with 91% pass rate — Dec 2025",
  "📚 Digital Literacy Integration Policy takes effect across all schools — Jan 2026",
  "🏫 278 schools monitored statewide in the 2025 inspection cycle",
  "🌟 12 Ogun teachers win national teaching excellence awards — Nov 2025",
  "💻 New E-Learning Portal launches with 50+ free courses for teachers",
  "📋 Principals & Vice Principals posting exercise announced — March 2026"
];

const QUICK_ACCESS = [
  {
    icon: "📢",
    label: "Whistle Blower",
    desc: "Tell us what's going on in your school",
    href: "https://wa.me/2348147578497"
  },
  {
    icon: "💬",
    label: "Issues downloading posting letter?",
    desc: "Click here to get help with your posting letter",
    href: "https://wa.me/+2348123283709"
  }
  // { icon: "📝", label: "Portal", desc: "Register and log in to access your profile", href: SIGNUP }
  // { icon: "📈", label: "Promotion Status", desc: "2,100 teachers eligible this cycle", href: "/promotion" },
  // { icon: "💻", label: "E-Learning", desc: "50+ free professional courses", href: "/elearning" }
];

const QUICK_NAV = [
  { id: "leadership", label: "Leadership" },
  { id: "about", label: "Vision & Values" },
  { id: "register", label: "The Register" },
  { id: "activities", label: "Activities" },
  { id: "what-we-do", label: "What We Do" },
  { id: "mentorship", label: "Mentorship" },
  { id: "structure", label: "Structure" },
  { id: "services", label: "Services" },
  { id: "recognition", label: "Recognition" },
  { id: "facilities", label: "Facilities" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" }
];

const STATS = [
  { num: "3,247+", label: "Teachers Assessed" },
  { num: "278", label: "Schools Monitored" },
  { num: "87%", label: "Competency Rate" },
  { num: "20", label: "LGA Coverage" }
];

const MILESTONES = [
  { num: "91%", label: "CBT pass rate for principal candidates", sub: "450+ candidates, Dec 2025" },
  { num: "278", label: "Schools monitored statewide", sub: "2025 inspection cycle" },
  {
    num: "8,000+",
    label: "Teachers supported through mentorship",
    sub: "Teacher Welfare directorate"
  },
  { num: "50+", label: "Free courses on the new E-Learning Portal", sub: "Launched December 2025" },
  {
    num: "2,100",
    label: "Teachers eligible for 2026 promotion",
    sub: "Exercise runs Apr – May 2026"
  },
  { num: "45", label: "School libraries renovated & upgraded", sub: "2025 school improvement plan" }
];

const TIMELINE = [
  {
    month: "Sep",
    year: "2026",
    tag: "Event",
    tagColor: "bg-[#eef4ee] text-[#14532d]",
    title: "TESCOM Clarifies Rural Postings Are Not a Punishment",
    body: "Addressing newly inducted school leaders, Chairman Apostle Abiodun Sanyaolu described postings to rural public secondary schools as a deliberate strategy for leadership development and equitable staff distribution, not disciplinary action."
  },
  {
    month: "Sep",
    year: "2026",
    tag: "Training",
    tagColor: "bg-[#e8eef7] text-[#1e4d8c]",
    title: "Zonal Coordinating Principals, Principals & Vice Principals Inducted",
    body: "TESCOM inducted 22 newly appointed Zonal Coordinating Principals and 131 Principals on September 7, followed by newly appointed Vice Principals on September 8, at Baptist Boys' High School, Saje, Abeokuta."
  },
  {
    month: "Sep",
    year: "2026",
    tag: "Programme",
    tagColor: "bg-[#f1ecf7] text-[#5b3a8c]",
    title: "OgunTEACH Interns Confirmed for Permanent Employment",
    body: "During the 2026 budget half-year appraisal before the State Assembly, Permanent Secretary Idowu Onasanya confirmed that OgunTEACH interns who completed their two-year internship have been permanently employed across public secondary schools."
  },
  {
    month: "Aug",
    year: "2026",
    tag: "Assessment",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "CBT & Screening for 564 Prospective Principals and Vice-Principals",
    body: "A two-day screening exercise and Computer-Based Test at Baptist Boys' High School, Saje, shortlisted 250 candidates for principal and 314 for vice-principal positions, drawn from officers on Grade Levels 16 and 17."
  },
  {
    month: "Apr",
    year: "2026",
    tag: "Assessment",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "CBT and Oral Interview for Prospective Principals",
    body: "Senior Vice Principals underwent Computer-Based Testing and oral interviews, with the topmost candidates serving in acting capacity pending formal induction."
  },
  {
    month: "Feb",
    year: "2026",
    tag: "Award",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "Ogun Teacher Named Nigeria's Overall Best Teacher",
    body: "Solanke Francis Taiwo, a primary school teacher in Abeokuta, won top honours at the National Teachers' Summit, receiving a ₦50 million prize, a two-bedroom flat and a pledged car."
  },
  {
    month: "Jun",
    year: "2026",
    tag: "Event",
    tagColor: "bg-[#eef4ee] text-[#14532d]",
    title: "National Teachers' Day Celebration — Nominations Now Open",
    body: "Annual celebration honouring teachers' contributions, with an awards ceremony, professional workshops and cultural events. Nominations close March 15."
  },
  {
    month: "Apr",
    year: "2026",
    tag: "Programme",
    tagColor: "bg-[#f1ecf7] text-[#5b3a8c]",
    title: "Annual Teacher Promotion Exercise Opens",
    body: "Approximately 2,100 teachers are eligible this cycle. Criteria include years of service, performance appraisals, professional qualifications and monitoring scores."
  },
  {
    month: "Mar",
    year: "2026",
    tag: "Event",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "Principals & Vice Principals Posting Exercise",
    body: "Successful CBT candidates and promoted school leaders are posted to schools across Ogun State, with postings weighed by experience, qualifications and school needs."
  },
  {
    month: "Feb",
    year: "2026",
    tag: "Training",
    tagColor: "bg-[#e8eef7] text-[#1e4d8c]",
    title:
      "Induction Programme for New Zonal Coordinating Principals, Principals & Vice Principals",
    body: "A 2-day intensive programme at the TESCOM Training Centre covering school leadership essentials, budget management, teacher supervision and community engagement."
  },
  {
    month: "Jan",
    year: "2026",
    tag: "Policy",
    tagColor: "bg-[#eef4ee] text-[#14532d]",
    title: "Digital Literacy Integration Policy Takes Effect",
    body: "All schools must integrate ICT into core subjects, with teachers receiving mandatory training on online tools and digital assessment."
  },
  {
    month: "Dec",
    year: "2025",
    tag: "Assessment",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "CBT Examination for Prospective Principals Concludes",
    body: "Over 450 prospective principals and vice principals completed the Computer-Based Test across 12 centres statewide, with a 91% pass rate."
  },
  {
    month: "Nov",
    year: "2025",
    tag: "Award",
    tagColor: "bg-[#fbf2dd] text-[#7a5b12]",
    title: "12 Ogun Teachers Win National Teaching Excellence Awards",
    body: "Recognised in Lagos for innovation, student impact and sustained professional commitment across their careers."
  }
];

const PROGRAMMES = [
  {
    icon: "🖥️",
    title: "CBT Recruitment & Assessment",
    desc: "Computer-based testing for principal, vice-principal and teacher candidates statewide."
  },
  {
    icon: "📈",
    title: "Promotion & Career Advancement",
    desc: "Transparent, merit-based promotion exercises for teaching staff at every level."
  },
  {
    icon: "💻",
    title: "Digital Learning & E-Portal",
    desc: "Free, self-paced professional development courses accessible to all teachers."
  },
  {
    icon: "🏫",
    title: "School Monitoring & Inspection",
    desc: "Regular visits ensuring teaching quality and compliance across every school."
  }
];

const FUNCTIONS = [
  {
    icon: "📜",
    title: "Recruitment & Selection",
    desc: "Conducting merit-based CBT recruitment for teaching and school leadership positions."
  },
  {
    icon: "⚖️",
    title: "Discipline & Conduct",
    desc: "Upholding professional standards and handling disciplinary matters for teaching staff."
  },
  {
    icon: "🔁",
    title: "Posting & Transfer",
    desc: "Coordinating staff postings and transfers across schools statewide."
  },
  {
    icon: "🤝",
    title: "Teacher Welfare & Support",
    desc: "Providing mentorship, wellness and welfare services to teaching staff."
  }
];
const VIDEOS = [
  {
    embedUrl:
      "https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fwatch%2F%3Fv%3D1947157785980893&show_text=false",
    label: "New Vice Principals Inducted",
    caption:
      "Apostle Biodun Sanyaolu explaining the rationale behind the Zonal Coordinating Principals Office"
    // "Newly appointed Vice Principals take their oath of office at Baptist Boys' High School, Saje, Abeokuta, charged by TESCOM leadership on professionalism.",
  },
  {
    embedUrl: "https://www.youtube.com/embed/J39WyfVZedo",
    label: "Zonal Coordinating Principals & Principals Take Oath",
    caption:
      "22 newly appointed Zonal Coordinating Principals and 131 Principals are sworn in as part of TESCOM's technology-driven leadership reforms."
  },
  {
    embedUrl:
      "https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fwatch%2F%3Fv%3D1026840076916383&show_text=false",
    label: "CBT Screening for 250 Senior Tutors",
    caption:
      "Inside the Computer-Based Test infrastructure used to screen senior tutors competing for Zonal Coordinating Principals, Principals and Vice Principal appointments across the state."
  }
]; // NOTE: placeholder cards — swap the play-button block in the video grid for a real <iframe> once URLs exist.

const OGUNTEACH_BENEFITS = [
  "Fast-tracked pathway into the public teaching service for new recruits",
  "Two-year supervised internship before consideration for permanent appointment",
  "₦80,000 monthly stipend paid throughout the internship period",
  "Priority for permanent absorption given to interns who serve diligently in rural postings",
  "Covers both public primary schools (via SUBEB) and public secondary schools (via TESCOM)"
];

const OGUNTEACH_STATS = [
  {
    num: "6,000+",
    label:
      "Teachers targeted for employment through OgunTEACH, with over 5,000 already given permanent appointments"
  },
  { num: "2 yrs", label: "Mandatory internship period before permanent appointment is considered" },
  { num: "20 LGAs", label: "Statewide coverage across every local government area in Ogun State" }
];
const EDUCATION_ZONES = [
  {
    name: "Zone 1 — Abeokuta",
    num: "120+",
    detail: "Abeokuta North & South, Odeda, Obafemi-Owode"
  },
  { name: "Zone 2 — Sagamu", num: "95+", detail: "Sagamu, Ikenne, Shagamu, Remo North" },
  {
    name: "Zone 3 — Ijebu-Ode",
    num: "110+",
    detail: "Ijebu-Ode, Ijebu-East, Ijebu-North, Odogbolu"
  },
  { name: "Zone 4 — Ota", num: "140+", detail: "Ado-Odo/Ota, Ifo, Ewekoro, Ipokia" },
  { name: "Zone 5 — Ilaro", num: "85+", detail: "Yewa North & South, Imeko-Afon, Ogun Waterside" }
];
// NOTE: school counts per zone are placeholders — confirm with Planning, Research & Statistics before publishing.

const DEPARTMENTS = [
  {
    slug: "Administrative function",
    icon: "📘",
    name: "Administration & Supplies",
    role: "Recruiment, Administrative welfare, Career Mangemnet, Promotion, e.t.c"
  },
  {
    slug: "Appeal & Disciplne",
    icon: "🤝",
    name: "Department of Appeal and Discipline",
    role: "Runs the appeal and disciplne of recalcitrant behaviours of staff and some aspects of career management."
  },
  {
    slug: "Posting of Teaching Staff  ",
    icon: "📈",
    name: "Department of Managemant Services",
    role: "Coordinates CBT recruitment of Principals and Vice-Principals and Induction Programmes"
  },
  {
    slug: "Finance and Account",
    icon: "🔁",
    name: "Departmenet of Finance and Accounts",
    role: "Manages staff salary, funds and financial records."
  },
  {
    slug: "planning-statistics",
    icon: "📊",
    name: "Planning, Research & Statistics",
    role: "Maintains staffing data, school records and workforce planning."
  },
  {
    slug: "ict-digitalization",
    icon: "💻",
    name: "Department of IT",
    role: "Creates and Mangages TESCOM PORTAL & Database and Digitalization of Tescom Programmes"
  }
];
// NOTE: department names/roles are inferred to match a typical teaching service commission structure — confirm against TESCOM's actual org chart.

const ORGANOGRAM = [
  { level: "Chairman, TESCOM", sub: "Political and policy leadership of the Commission" },
  {
    level: "Permanent Secretary",
    sub: "Administrative head, coordinates departments and zonal offices"
  },
  {
    level: "Directors / Heads of Department",
    sub: "Human Resource · Teacher Welfare · Recruitment & Promotion · Postings · Planning · ICT"
  },
  {
    level: "Zonal Coordinating Principals",
    sub: "Policy Implementation and Monitoring Across All 22 zones"
  },
  {
    level: "Schools & Teaching Staff",
    sub: "Principals, vice principals and teachers across all 20 LGAs"
  }
];
// NOTE: illustrative structure — replace with TESCOM's verified organogram.

const SUPPORT_CARDS = [
  {
    icon: "🖥️",
    title: "CBT Portal",
    desc: "Prepare for computer-based recruitment, Staff Data  Manangement and E-Letters."
  },
  {
    icon: "📋",
    title: "Posting & Transfer Support",
    desc: "Download posting letters and promotion letters through the staff portal."
  },
  {
    icon: "🩺",
    title: "Welfare & Pension Assistance",
    desc: "Guidance on welfare benefits, pension processing and support services for retiring staff."
  }
];

const AWARDS = [
  {
    icon: "🏆",
    title: "12 Teachers Win National Teaching Excellence Awards",
    body: "Recognised at the National Teaching Excellence Summit in Lagos for innovation, student impact and sustained professional commitment.",
    date: "November 2025"
  },
  {
    icon: "🎓",
    title: "91% Pass Rate — CBT Examination for Principal Candidates",
    body: "Over 450 prospective principals and vice principals sat the Computer-Based Test across 12 centres statewide.",
    date: "December 2025"
  },
  {
    icon: "🔬",
    title: "28% Rise in Female STEM Enrolment",
    body: "TESCOM's Girls in STEM initiative increased female student enrolment in physics and engineering programmes across participating schools.",
    date: "2025"
  },
  {
    icon: "📖",
    title: "45 School Libraries Renovated & Upgraded",
    body: "Libraries now feature reading corners, computer stations and digital learning resources as part of the 2025 school improvement plan.",
    date: "November 2025"
  }
];
const NEWS = [
  {
    tag: "Event",
    title: "TESCOM Chairman: Rural Postings for New Vice Principals Not a Punishment",
    date: "September 11, 2026"
  },
  {
    tag: "Training",
    title: "22 Zonal Coordinating Principals and 131 Principals Inducted, Vice Principals Follow",
    date: "September 7–9, 2026"
  },
  {
    tag: "Programme",
    title: "OgunTEACH Interns Confirmed for Permanent Employment at Budget Appraisal",
    date: "September 2026"
  },
  {
    tag: "Assessment",
    title: "TESCOM Screens 564 Senior Teachers for Principal and Vice-Principal Roles",
    date: "August 2026"
  },
  {
    tag: "Welfare",
    title: "TESCOM Lauds Federal Mortgage Bank's Housing Loan Initiative for Teaching Workforce",
    date: "August 2026"
  },
  {
    tag: "Digital",
    title: "OgunLEARN Competition Rewards Top 10 Teachers for Digital Innovation",
    date: "June 9, 2026"
  }
];
const FACILITIES = [
  {
    icon: "🏫",
    name: "TESCOM Training Centre",
    type: "Leadership Training",
    desc: "Hosts induction and leadership training programmes for newly posted principals and vice principals.",
    stats: [
      { n: "5-day", l: "Induction" },
      { n: "450+", l: "Trained/yr" },
      { n: "Abeokuta", l: "Location" }
    ]
  },
  {
    icon: "🖥️",
    name: "CBT Examination Centre Network",
    type: "Assessment Infrastructure",
    desc: "Computer-based testing infrastructure used for recruitment and promotion examinations statewide.",
    stats: [
      { n: "12", l: "Centres" },
      { n: "91%", l: "Pass rate" },
      { n: "2025", l: "Cycle" }
    ]
  },
  {
    icon: "💻",
    name: "Digital Learning Hub / E-Library",
    type: "Online Platform",
    desc: "Hosts the E-Learning portal with 50+ free professional development courses for teachers.",
    stats: [
      { n: "50+", l: "Courses" },
      { n: "Online", l: "Access" },
      { n: "2025", l: "Launched" }
    ]
  },
  {
    icon: "🏢",
    name: "Zonal Education Offices",
    type: "Regional Administration",
    desc: "Regional administrative offices coordinating schools and staff across all 20 local government areas.",
    stats: [
      { n: "5", l: "Zones" },
      { n: "20", l: "LGAs" },
      { n: "Statewide", l: "Reach" }
    ]
  },
  {
    icon: "🔬",
    name: "ICT Innovation Centre",
    type: "Digital Policy Hub",
    desc: "Supports the rollout of the Digital Literacy Integration Policy across all schools.",
    stats: [
      { n: "2026", l: "Policy live" },
      { n: "All", l: "Schools" },
      { n: "ICT", l: "Focus" }
    ]
  }
];

const PARTNERS = [
  { name: "Ogun State Ministry of Education", short: "MoE", icon: "🏛️" },
  { name: "State Universal Basic Education Board", short: "SUBEB", icon: "🏫" },
  { name: "Teachers Registration Council of Nigeria", short: "TRCN", icon: "🎓" },
  { name: "National Teachers' Institute", short: "NTI", icon: "📘" },
  { name: "Nigeria Union of Teachers, Ogun State", short: "NUT", icon: "🤝" },
  { name: "UNICEF — Teacher Training & Education Support", short: "UNICEF", icon: "🌍" },
  { name: "World Bank — AGILE Education Programme", short: "World Bank", icon: "🏦" },
  { name: "British Council — School Leadership Development", short: "British Council", icon: "🎗️" }
];
// NOTE: illustrative list — confirm formal partnerships/handles before publishing.

const FAQS = [
  {
    q: "How do I check my posting letter?",
    a: "Posting letter are published through the staff portal and zonal offices after each posting exercise. Login to your account if you haven't received a letter."
  },
  {
    q: "What are the eligibility criteria for promotion?",
    a: "Promotion criteria include years of service, performance appraisals, professional qualifications and monitoring scores. Full criteria are published ahead of each promotion cycle."
  },
  {
    q: "How do I register for the CBT exam?",
    a: "Invitations are sent ahead of each recruitment or promotion cycle via the CBT Portal, with instructions on required documents and test centres."
  },

  {
    q: "Where do I report a welfare complaint or concern?",
    a: "Welfare concerns can be raised confidentially through the whistle blower platform on the staff portal."
  }
];

const LEADERS = [
  {
    key: "governor",
    icon: "🏛️",
    name: "H.E. Prince Dapo Abiodun",
    title: "Executive Governor, Ogun State",
    tags: ["Since 2019", "CON"],
    quote: "I know very much the sacrifices that teachers make daily to impart knowledge.",
    focus: "State-level policy direction for education and teacher welfare.",
    verified: true
  },
  {
    key: "chairman",
    icon: "🎓",
    name: "Apostle Abiodun Sanyaolu",
    title: "Chairman, TESCOM",
    tags: ["Policy Lead"],
    quote: "Continue to uphold the ethics of their professions and be dutiful.",
    focus: "Recruitment, promotion, discipline and welfare policy for teaching staff.",
    contact: "",
    verified: true
  },
  {
    key: "secretary",
    icon: "📋",
    name: "Mr. Idowu Onasanya",
    title: "Permanent Secretary, TESCOM",
    tags: ["Administration"],
    quote:
      "Speaking on induction of new principals, he called for safe, disciplined, stimulating learning environments where teachers and learners can thrive.",
    focus: "Administrative oversight of departments, zonal offices and ICT rollout.",
    contact: "",
    verified: false
  }
];

const CORE_VALUES = [
  {
    icon: "🤝",
    title: "Equity",
    desc: "Fair recruitment, posting and promotion for every teacher, regardless of location or background."
  },
  {
    icon: "🔍",
    title: "Accountability",
    desc: "Transparent, merit-based processes and data-driven decision-making at every level."
  },
  {
    icon: "⭐",
    title: "Quality",
    desc: "Continuous improvement of teaching standards and classroom outcomes across facilities."
  },
  {
    icon: "💡",
    title: "Innovation",
    desc: "Embracing digital tools like CBT and e-learning to solve persistent workforce gaps."
  },
  {
    icon: "📚",
    title: "Professionalism",
    desc: "Upholding standards of conduct and continuous development for every teacher."
  },
  {
    icon: "❤️",
    title: "Welfare",
    desc: "Treating every teacher with dignity, mentorship, and genuine wellbeing support."
  }
];

// ─── Magazine modal ──────────────────────────────────────────────────────

function MagazineModal({ issue, onClose }: { issue: MagazineIssue | null; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl w-full max-w-5xl h-[85vh] overflow-hidden shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close magazine"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-[#0a2e1a] text-[#E8C468] flex items-center justify-center hover:bg-[#0f3d23] transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <TESCOMMagazine issueId={issue?.id} />
      </motion.div>
    </motion.div>
  );
}

// // ─── Main component ────────────────────────────────────────────────────────

export default function TescomLandingPage() {
  const [readProgress, setReadProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [issue, setIssue] = useState<MagazineIssue | null>(null);
  const [magazineOpen, setMagazineOpen] = useState(false);
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(QUICK_NAV[0].id);
  const [whatWeDoTab, setWhatWeDoTab] = useState("Programmes");
  const [structureTab, setStructureTab] = useState("Zones");

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    fetchLatestIssue()
      .then(setIssue)
      .catch(() => setIssue(null));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setReadProgress(progress);
      setShowBackToTop(scrollTop > 600);
      setShowQuickNav(scrollTop > 520);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    QUICK_NAV.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const registerRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const whatWeDoItems = useMemo(
    () => (whatWeDoTab === "Programmes" ? PROGRAMMES : FUNCTIONS),
    [whatWeDoTab]
  );

  return (
    <>
      <style>{FONT_IMPORTS}</style>
      <Navbar />
      <main className="bg-[#FAF7EF] text-[#1C2320] relative">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:bg-white focus:text-[#0a2e1a] focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
        >
          Skip to content
        </a>

        {/* ── READ PROGRESS BAR ────────────────────────────────────────── */}
        <div className="fixed top-0 left-0 right-0 h-1 bg-black/10 z-[60]">
          <motion.div
            className="h-full bg-[#C9971C]"
            style={{ width: `${readProgress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        {/* ── STICKY QUICK NAV (folder tabs) ───────────────────────────── */}
        <AnimatePresence>
          {showQuickNav && (
            <motion.nav
              initial={{ y: -48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -48, opacity: 0 }}
              transition={{ duration: 0.2 }}
              aria-label="Section navigation"
              className="fixed top-1 left-0 right-0 z-[55] bg-[#0a2e1a]/95 backdrop-blur border-b border-white/10"
            >
              <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto px-4 py-2 no-scrollbar">
                {QUICK_NAV.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollToSection(s.id)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-t-md text-[11px] font-semibold tracking-wide transition-colors ${
                      activeSection === s.id
                        ? "bg-[#FAF7EF] text-[#0a2e1a]"
                        : "text-white/55 hover:text-white hover:bg-white/[0.08]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>

        <div id="main-content">
          {/* ── HERO ────────────────────────────────────────────────────── */}
          <section className="relative bg-[#0a2e1a] pt-24 pb-14 px-6 overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
                backgroundSize: "56px 56px"
              }}
            />
            <div className="relative max-w-5xl mx-auto text-center">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={0}
                className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/15 rounded-full px-4 py-1.5 text-[11px] font-semibold tracking-widest uppercase text-white/70 mb-5 tsc-mono"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#7fd4a0] animate-pulse" />
                Official Government Portal
              </motion.div>

              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={1}
                className="tsc-display text-4xl sm:text-5xl lg:text-[3.4rem] font-semibold text-white leading-[1.1] max-w-3xl mx-auto mb-4"
              >
                The record and the reach of <span className="text-[#E8C468]">every teacher</span> in
                Ogun State
              </motion.h1>

              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={2}
                className="text-[15px] text-white/75 max-w-xl mx-auto leading-relaxed mb-9"
              >
                TESCOM recruits, promotes, trains and supports teaching staff across all 20 local
                government areas — the register behind every classroom in the state.
              </motion.p>

              {/* Task-first quick access — the actual job most visitors came to do */}
              <ScrollReveal variants={fadeUp} custom={3}>
                <div className="max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {QUICK_ACCESS.slice(0, 2).map((q) => (
                    <Link
                      key={q.label}
                      to={q.href}
                      className="group flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 hover:border-[#E8C468]/40 rounded-xl p-3.5 transition-colors text-left"
                    >
                      <div className="text-2xl shrink-0">{q.icon}</div>
                      <div className="min-w-0">
                        <p className="text-white text-lg font-semibold leading-snug mb-0.5 ">
                          {q.label}
                        </p>
                        <p className="text-white/45 text-xs leading-snug line-clamp-2">{q.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollReveal>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={4}
                className="flex flex-wrap gap-3 justify-center mt-8"
              >
                <Link
                  to="#"
                  className="bg-[#C9971C] text-[#1C2320] font-semibold text-sm px-7 py-3 rounded-lg hover:bg-[#E8C468] transition-colors"
                >
                  Our Programmes
                </Link>
                <button
                  onClick={() => setMagazineOpen(true)}
                  className="bg-transparent text-white font-medium text-sm px-7 py-3 rounded-lg border border-white/25 hover:border-white/50 transition-colors"
                >
                  Read the Magazine
                </button>
              </motion.div>
            </div>
          </section>

          {/* ── NEWS TICKER ───────────────────────────────────────────────── */}
          <div className="bg-[#C9971C] overflow-hidden py-2.5">
            <div
              className="flex whitespace-nowrap tsc-ticker-track"
              style={{ animation: "tsc-ticker 32s linear infinite" }}
            >
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
                <span
                  key={i}
                  className="inline-block px-8 text-[11px] font-semibold text-[#1C2320]"
                >
                  {item} <span className="opacity-40 mx-2">•</span>
                </span>
              ))}
            </div>
          </div>

          {/* ── STATS LEDGER ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#e4ddc9] bg-white border-b border-[#e4ddc9]">
            {STATS.map((s, i) => (
              <ScrollReveal key={s.label} custom={i} className="py-8 px-6 text-center">
                <p className="tsc-mono text-3xl font-semibold text-[#0a2e1a] leading-none">
                  {s.num}
                </p>
                <p className="text-[11px] text-[#5b6560] mt-2 tracking-wide">{s.label}</p>
              </ScrollReveal>
            ))}
          </div>

          {/* ── MAGAZINE STRIP ───────────────────────────────────────────── */}
          <section className="bg-white py-14 px-6 border-b border-[#e4ddc9]">
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center gap-6 bg-[#FAF7EF] border border-[#e4ddc9] rounded-2xl p-6">
              <div className="w-24 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#0a2e1a] to-[#14532d]">
                {issue?.coverImageUrl && (
                  <img
                    src={issue.coverImageUrl}
                    alt={issue.title}
                    className="w-full h-full object-cover opacity-90"
                  />
                )}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="tsc-mono text-[10px] font-bold tracking-widest uppercase text-[#7a5b12] mb-1.5">
                  Digital Magazine · Latest Issue
                </p>
                <p className="tsc-display font-semibold text-[#0a2e1a] text-lg mb-1">
                  {issue?.title ?? "TESCOM Magazine"}
                </p>
                <p className="text-xs text-[#8a9088]">
                  {issue?.totalPages ?? 28} pages · flip-book view
                </p>
              </div>
              <button
                onClick={() => setMagazineOpen(true)}
                className="bg-[#C9971C] text-[#1C2320] font-semibold text-sm px-6 py-3 rounded-lg hover:bg-[#E8C468] transition-colors whitespace-nowrap"
              >
                Open Magazine
              </button>
            </div>
          </section>

          {/* ── LEADERSHIP (merged Governor + Chairman + Secretary) ─────── */}
          <section
            id="leadership"
            ref={registerRef("leadership")}
            className="bg-white py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="TESCOM leadership"
                title="Leadership"
                sub="Political and administrative leadership shaping recruitment, promotion, discipline and welfare policy for every teacher in the state."
              />
              <div className="grid md:grid-cols-3 gap-5">
                {LEADERS.map((l, i) => (
                  <ScrollReveal key={l.key} custom={i}>
                    <div className="bg-[#FAF7EF] border border-[#e4ddc9] rounded-xl p-6 h-full flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 rounded-xl bg-white border border-[#e4ddc9] flex items-center justify-center text-2xl">
                          {l.icon}
                        </div>
                        {l.verified && <VerifiedStamp />}
                      </div>
                      <p className="font-semibold text-[#14201a] text-base leading-snug">
                        {l.name}
                      </p>
                      <p className="text-xs text-[#7a5b12] mt-0.5 mb-3">{l.title}</p>
                      <div className="flex gap-1.5 flex-wrap mb-4">
                        {l.tags.map((t) => (
                          <span
                            key={t}
                            className="bg-white border border-[#e4ddc9] rounded-full px-2.5 py-0.5 text-[10px] font-semibold text-[#5b6560]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-[#5b6560] leading-relaxed mb-4 flex-1">
                        {l.focus}
                      </p>
                      <blockquote className="border-l-2 border-[#C9971C] pl-3">
                        <p className="text-[11px] text-[#8a9088] italic leading-relaxed">
                          {l.quote}
                        </p>
                      </blockquote>
                      {l.contact && (
                        <p className="tsc-mono text-[10px] text-[#8a9088] mt-3">{l.contact}</p>
                      )}
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── VISION, MISSION & VALUES ─────────────────────────────────── */}
          <section
            id="about"
            ref={registerRef("about")}
            className="bg-[#FAF7EF] py-20 px-6 border-t border-[#e4ddc9] scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="What guides us"
                title="Vision, Mission & Core Values"
                sub="The principles that shape every policy, programme and posting under the Commission."
              />
              <div className="grid md:grid-cols-2 gap-5 mb-10">
                <ScrollReveal>
                  <div className="bg-gradient-to-br from-[#0a2e1a] to-[#14532d] rounded-2xl p-7 h-full">
                    <span className="tsc-mono inline-block bg-[#E8C468]/15 border border-[#E8C468]/30 rounded-md px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase text-[#E8C468] mb-4">
                      Vision
                    </span>
                    <p className="text-white text-lg leading-relaxed">
                      To build a teaching workforce in Ogun State that is well-trained,
                      well-supported and led with integrity — so every child has access to quality
                      education.
                    </p>
                  </div>
                </ScrollReveal>
                <ScrollReveal custom={1}>
                  <div className="bg-white border border-[#e4ddc9] rounded-2xl p-7 h-full">
                    <span className="tsc-mono inline-block bg-[#eef4ee] border border-[#c9e0cd] rounded-md px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase text-[#14532d] mb-4">
                      Mission
                    </span>
                    <p className="text-[#3a423d] text-lg leading-relaxed">
                      The Teaching Service Commission exists to recruit, deploy, promote, discipline
                      and welfare-support teaching staff across Ogun State through transparency,
                      professionalism and accountability to the people of the State.
                    </p>
                  </div>
                </ScrollReveal>
              </div>
              {/* NOTE: vision/mission wording is illustrative — confirm against TESCOM's official statement before publishing. */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {CORE_VALUES.map((v, i) => (
                  <ScrollReveal key={v.title} custom={i}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white border border-[#e4ddc9] rounded-xl p-6 h-full flex flex-col"
                    >
                      <div className="text-2xl mb-3">{v.icon}</div>
                      <h3 className="font-semibold text-[#0a2e1a] text-sm mb-2">{v.title}</h3>
                      <p className="text-xs text-[#5b6560] leading-relaxed">{v.desc}</p>
                    </motion.div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── THE REGISTER (milestones) ────────────────────────────────── */}
          <section
            id="register"
            ref={registerRef("register")}
            className="bg-[#0a2e1a] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Verified outcomes"
                title={<>The Register</>}
                sub="Documented, verifiable outcomes from the Commission's recent recruitment, training and welfare cycles."
                light
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {MILESTONES.map((m, i) => (
                  <ScrollReveal key={m.label} custom={i} variants={fadeIn}>
                    <motion.div
                      whileHover={{ backgroundColor: "rgba(255,255,255,0.1)" }}
                      className="relative bg-white/[0.06] border border-white/10 rounded-xl p-5 transition-colors"
                    >
                      <VerifiedStamp className="absolute top-4 right-4" />
                      <p className="tsc-mono text-3xl font-semibold text-[#E8C468] leading-none">
                        {m.num}
                      </p>
                      <p className="text-xs text-white/85 mt-2 leading-relaxed pr-6">{m.label}</p>
                      <p className="text-[10px] text-[#9fcf9f] mt-1">{m.sub}</p>
                    </motion.div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── RECENT ACTIVITIES TIMELINE ───────────────────────────────── */}
          <section
            id="activities"
            ref={registerRef("activities")}
            className="bg-[#FAF7EF] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-3xl mx-auto">
              <SectionHeader
                eyebrow="2025 – 2026"
                title="Recent Activities & Events"
                sub="A chronological record of the Commission's most significant actions and events."
              />
              <div className="space-y-0">
                {TIMELINE.map((item, i) => (
                  <ScrollReveal key={item.title} custom={i} className="flex gap-4">
                    <div className="w-14 flex-shrink-0 text-right pt-1">
                      <p className="tsc-mono text-[10px] font-semibold uppercase tracking-wider text-[#7a5b12]">
                        {item.month}
                      </p>
                      <p className="text-[10px] text-[#8a9088]">{item.year}</p>
                    </div>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#14532d] mt-1.5 flex-shrink-0" />
                      {i < TIMELINE.length - 1 && <div className="w-px flex-1 bg-[#e4ddc9] my-1" />}
                    </div>
                    <div className="pb-8 flex-1">
                      <span
                        className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-2 ${item.tagColor}`}
                      >
                        {item.tag}
                      </span>
                      <p className="text-sm font-semibold text-[#14201a] mb-1.5 leading-snug">
                        {item.title}
                      </p>
                      <p className="text-xs text-[#5b6560] leading-relaxed">{item.body}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── WHAT WE DO (merged Programmes + Functions) ───────────────── */}
          <section
            id="what-we-do"
            ref={registerRef("what-we-do")}
            className="bg-white py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
                <SectionHeader
                  eyebrow="What we do"
                  title="Programmes & Statutory Functions"
                  sub="Flagship initiatives and the statutory duties that back them, side by side."
                />
                <ScrollReveal>
                  <SegmentedToggle
                    options={["Programmes", "Functions"]}
                    value={whatWeDoTab}
                    onChange={setWhatWeDoTab}
                  />
                </ScrollReveal>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {whatWeDoItems.map((p, i) => (
                  <ScrollReveal key={p.title} custom={i}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="bg-[#FAF7EF] border border-[#e4ddc9] border-t-4 border-t-[#0a2e1a] rounded-xl p-6 h-full"
                    >
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-xl mb-4">
                        {p.icon}
                      </div>
                      <h3 className="font-semibold text-[#14201a] text-sm mb-2 leading-snug">
                        {p.title}
                      </h3>
                      <p className="text-xs text-[#5b6560] leading-relaxed">{p.desc}</p>
                    </motion.div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── VIDEO SHOWCASE ────────────────────────────────────────────── */}
          <section className="bg-[#FAF7EF] py-20 px-6">
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Watch & learn"
                title="TESCOM in Action"
                sub="Video coverage of recruitment, training and school activities across Ogun State."
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {VIDEOS.map((v, i) => (
                  <ScrollReveal key={v.label} custom={i}>
                    <div>
                      <div className="rounded-xl overflow-hidden aspect-video bg-[#0a2e1a] border border-[#e4ddc9]">
                        <iframe
                          className="w-full h-full"
                          src={v.embedUrl}
                          title={v.label}
                          loading="lazy"
                          style={{ border: "none", overflow: "hidden" }}
                          scrolling="no"
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                      <p className="tsc-mono text-[10px] font-bold uppercase tracking-widest text-[#7a5b12] mt-3 mb-1">
                        {v.label}
                      </p>
                      <p className="text-xs text-[#5b6560] leading-relaxed">{v.caption}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>
          {/* ── FLAGSHIP: MENTORSHIP ─────────────────────────────────────── */}
          {/* ── FLAGSHIP: OGUNTEACH ─────────────────────────────────────── */}
          <section
            id="ogunteach"
            ref={registerRef("ogunteach")}
            className="py-20 px-6 scroll-mt-20"
            style={{ background: "linear-gradient(135deg,#0a2e1a,#1a5c30)" }}
          >
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
              <ScrollReveal variants={fadeLeft}>
                <span className="tsc-mono inline-block bg-[#E8C468]/15 border border-[#E8C468]/30 rounded-md px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase text-[#E8C468] mb-4">
                  Flagship Programme
                </span>
                <h2 className="tsc-display !text-white text-3xl md:text-4xl font-semibold leading-tight mb-4">
                  OgunTEACH — Teacher Recruitment & Internship Pathway
                </h2>
                <p className="text-sm text-white/65 leading-relaxed mb-5">
                  The Ogun State Teaching Experience Acquisition Channel fast-tracks new teachers
                  into public primary and secondary schools through a stipend-paid, two-year
                  internship, with permanent appointment into the Teaching Service on successful
                  completion — closing the state's teacher-to-student ratio gap.
                </p>
                <ul className="space-y-3">
                  {OGUNTEACH_BENEFITS.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                      <div className="w-5 h-5 rounded-full bg-[#7fd4a0] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg
                          className="w-2.5 h-2.5 text-[#0a2e1a]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3}
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
              <ScrollReveal custom={1}>
                <div className="space-y-4">
                  {OGUNTEACH_STATS.map((s) => (
                    <div
                      key={s.num}
                      className="bg-white/[0.08] border border-white/15 rounded-xl p-5"
                    >
                      <p className="tsc-mono text-3xl font-semibold text-[#E8C468]">{s.num}</p>
                      <p className="text-xs text-white/55 mt-1.5 leading-relaxed">{s.label}</p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </section>

          {/* ── STRUCTURE (merged Zones + Departments + Organogram) ─────── */}
          <section
            id="structure"
            ref={registerRef("structure")}
            className="bg-[#FAF7EF] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
                <SectionHeader
                  eyebrow="How TESCOM is organised"
                  title="Structure & Reach"
                  sub="Zonal coverage, departments and the reporting line, in one place instead of three."
                />
                <ScrollReveal>
                  <SegmentedToggle
                    options={["Zones", "Departments", "Org Chart"]}
                    value={structureTab}
                    onChange={setStructureTab}
                  />
                </ScrollReveal>
              </div>

              {structureTab === "Zones" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {EDUCATION_ZONES.map((z, i) => (
                    <ScrollReveal key={z.name} custom={i} variants={fadeIn}>
                      <div className="bg-white border border-[#e4ddc9] border-t-4 border-t-[#0a2e1a] rounded-xl p-5">
                        <p className="text-sm font-semibold text-[#14201a] mb-2">{z.name}</p>
                        <p className="tsc-mono text-2xl font-semibold text-[#3a423d]">{z.num}</p>
                        <p className="text-[11px] text-[#5b6560] mt-1.5 leading-relaxed">
                          {z.detail}
                        </p>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {structureTab === "Departments" && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {DEPARTMENTS.map((d, i) => (
                    <ScrollReveal key={d.slug} custom={i}>
                      <Link to={`/departments/${d.slug}`}>
                        <motion.div
                          whileHover={{ y: -4 }}
                          transition={{ duration: 0.2 }}
                          className="bg-white border border-[#e4ddc9] border-t-4 border-t-[#0a2e1a] rounded-xl p-6 h-full"
                        >
                          <div className="w-10 h-10 bg-[#FAF7EF] rounded-xl flex items-center justify-center text-xl mb-4">
                            {d.icon}
                          </div>
                          <h3 className="font-semibold text-[#14201a] text-sm mb-2 leading-snug">
                            {d.name}
                          </h3>
                          <p className="text-xs text-[#5b6560] leading-relaxed">{d.role}</p>
                        </motion.div>
                      </Link>
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {structureTab === "Org Chart" && (
                <div className="max-w-2xl">
                  {ORGANOGRAM.map((tier, i) => (
                    <ScrollReveal key={tier.level} custom={i} className="flex gap-4">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-3 h-3 rounded-full bg-[#C9971C] mt-1.5 flex-shrink-0" />
                        {i < ORGANOGRAM.length - 1 && (
                          <div className="w-px flex-1 bg-[#e4ddc9] my-1" />
                        )}
                      </div>
                      <div className="pb-8 flex-1">
                        <p className="text-sm font-semibold text-[#14201a] mb-1">{tier.level}</p>
                        <p className="text-xs text-[#5b6560] leading-relaxed">{tier.sub}</p>
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* ── SUPPORT SERVICES ──────────────────────────────────────────── */}
          <section
            id="services"
            ref={registerRef("services")}
            className="bg-[#eef4ee] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Support for staff"
                title="Staff Support Services"
                sub="Digital tools and support channels available to every teacher on TESCOM's payroll."
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {SUPPORT_CARDS.map((c, i) => (
                  <ScrollReveal key={c.title} custom={i}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white border border-[#c9e0cd] rounded-xl p-6 h-full"
                    >
                      <div className="text-2xl mb-3">{c.icon}</div>
                      <h3 className="font-semibold text-[#0a2e1a] text-sm mb-2">{c.title}</h3>
                      <p className="text-xs text-[#5b6560] leading-relaxed">{c.desc}</p>
                    </motion.div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── RECOGNITION (merged Awards + News) ───────────────────────── */}
          <section
            id="recognition"
            ref={registerRef("recognition")}
            className="bg-white py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Verified & in the news"
                title="Recognition & Updates"
                sub="Independent recognition of Ogun State's progress, alongside the latest announcements from the Commission."
              />
              <div className="grid lg:grid-cols-2 gap-10">
                <div className="space-y-4">
                  <p className="tsc-mono text-[11px] tracking-widest uppercase text-[#7a5b12] mb-1">
                    Awards
                  </p>
                  {AWARDS.map((a, i) => (
                    <ScrollReveal key={a.title} custom={i}>
                      <motion.div
                        whileHover={{ x: 4 }}
                        transition={{ duration: 0.2 }}
                        className="relative bg-[#FBF2DD] border border-[#f0dfb0] rounded-xl p-5 flex gap-4 items-start"
                      >
                        <div className="w-11 h-11 rounded-xl bg-[#C9971C] flex items-center justify-center text-xl flex-shrink-0">
                          {a.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-[#14201a] text-sm mb-1.5">{a.title}</p>
                            <VerifiedStamp className="mt-0.5" />
                          </div>
                          <p className="text-xs text-[#5b6560] leading-relaxed">{a.body}</p>
                          <p className="tsc-mono text-[10px] font-semibold uppercase tracking-wider text-[#a3752a] mt-2">
                            {a.date}
                          </p>
                        </div>
                      </motion.div>
                    </ScrollReveal>
                  ))}
                </div>
                <div>
                  <p className="tsc-mono text-[11px] tracking-widest uppercase text-[#7a5b12] mb-4">
                    Latest news
                  </p>
                  <div className="space-y-4">
                    {NEWS.map((n, i) => (
                      <ScrollReveal key={n.title} custom={i}>
                        <motion.div
                          whileHover={{ x: 4 }}
                          transition={{ duration: 0.2 }}
                          className="bg-[#FAF7EF] border border-[#e4ddc9] rounded-xl p-5 cursor-pointer"
                        >
                          <p className="tsc-mono text-[10px] font-bold tracking-widest uppercase text-[#14532d] mb-2">
                            {n.tag}
                          </p>
                          <h4 className="text-sm font-semibold text-[#14201a] leading-snug mb-3">
                            {n.title}
                          </h4>
                          <p className="text-xs text-[#8a9088]">{n.date}</p>
                        </motion.div>
                      </ScrollReveal>
                    ))}
                  </div>
                  <ScrollReveal>
                    <Link
                      to="/news"
                      className="inline-block mt-4 text-sm text-[#14532d] font-medium hover:underline"
                    >
                      View all news →
                    </Link>
                  </ScrollReveal>
                </div>
              </div>
            </div>
          </section>

          {/* ── FACILITIES ────────────────────────────────────────────────── */}
          <section
            id="facilities"
            ref={registerRef("facilities")}
            className="bg-[#FAF7EF] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Flagship facilities"
                title="Key Facilities"
                sub="Scroll to explore the facilities and infrastructure supporting teacher development."
              />
              <ScrollReveal>
                <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory">
                  {FACILITIES.map((f) => (
                    <motion.div
                      key={f.name}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="min-w-[260px] snap-start bg-white border border-[#e4ddc9] rounded-xl p-5 flex-shrink-0"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-[#eef4ee] flex items-center justify-center text-base flex-shrink-0">
                          {f.icon}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#14201a] leading-snug">
                            {f.name}
                          </p>
                          <p className="text-[10px] text-[#8a9088] uppercase tracking-wider">
                            {f.type}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-[#5b6560] leading-relaxed mb-3">{f.desc}</p>
                      <div className="flex gap-2">
                        {f.stats.map((s) => (
                          <div key={s.l} className="flex-1 bg-[#FAF7EF] rounded-md p-2 text-center">
                            <p className="tsc-mono text-sm font-semibold text-[#0a2e1a]">{s.n}</p>
                            <p className="text-[9px] text-[#8a9088] mt-0.5">{s.l}</p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </section>

          {/* ── PARTNERS & COLLABORATORS (merged agencies + partners) ────── */}
          <section className="bg-[#0a2e1a] py-20 px-6">
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow="Related bodies"
                title={<>Partners & Collaborators</>}
                sub="Agencies and development partners TESCOM coordinates with on recruitment, standards and teacher development."
                light
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {PARTNERS.map((a, i) => (
                  <ScrollReveal key={a.name} custom={i} variants={fadeIn}>
                    <motion.div
                      whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.1)" }}
                      transition={{ duration: 0.2 }}
                      className="bg-white/[0.06] border border-white/10 rounded-xl p-4 h-full group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#E8C468]/15 flex items-center justify-center text-lg mb-3">
                        {a.icon}
                      </div>
                      <p className="text-[11px] text-white/65 leading-relaxed transition-colors duration-200 group-hover:text-white">
                        {a.name} <span className="text-white/40">· {a.short}</span>
                      </p>
                    </motion.div>
                  </ScrollReveal>
                ))}
              </div>
              {/* NOTE: illustrative list — confirm active partnerships before publishing. */}
            </div>
          </section>

          {/* ── FAQ ───────────────────────────────────────────────────────── */}
          <section
            id="faq"
            ref={registerRef("faq")}
            className="bg-[#FAF7EF] py-20 px-6 scroll-mt-20"
          >
            <div className="max-w-3xl mx-auto">
              <SectionHeader
                eyebrow="Common questions"
                title="Frequently Asked Questions"
                sub="Quick answers about postings, promotion, CBT and how to reach TESCOM."
              />
              <div className="space-y-3">
                {FAQS.map((f, i) => (
                  <ScrollReveal key={f.q} custom={i}>
                    <FaqItem
                      q={f.q}
                      a={f.a}
                      isOpen={openFaq === i}
                      onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                    />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA + CONTACT ─────────────────────────────────────────────── */}
          <section
            id="contact"
            ref={registerRef("contact")}
            className="bg-[#0a2e1a] py-20 px-6 text-center scroll-mt-20"
          >
            <ScrollReveal>
              <p className="tsc-mono text-xs font-semibold tracking-widest uppercase text-[#9fcf9f] mb-3">
                Get in touch
              </p>
              <h2 className="tsc-display text-3xl md:text-4xl font-semibold !text-white mb-4">
                Need posting, promotion or CBT information?
              </h2>
              <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">
                Reach our helpline or visit any TESCOM zonal office across Ogun State.
              </p>
              <div className="flex flex-wrap gap-3 justify-center mb-12">
                <Link
                  to="/contact"
                  className="inline-block bg-[#C9971C] text-[#1C2320] font-semibold text-sm px-9 py-3.5 rounded-lg hover:bg-[#E8C468] transition-colors"
                >
                  Contact Us
                </Link>
                <button
                  onClick={() => setMagazineOpen(true)}
                  className="inline-block bg-transparent text-white font-semibold text-sm px-9 py-3.5 rounded-lg border-2 border-white/25 hover:border-white/60 transition-colors"
                >
                  Read the Magazine
                </button>
              </div>
            </ScrollReveal>

            <div className="max-w-3xl mx-auto grid sm:grid-cols-3 gap-4 mb-12">
              {[
                {
                  icon: "📞",
                  label: "Helpline",
                  value: "[Insert phone number]",
                  sub: "Staff & public enquiries"
                },
                {
                  icon: "📍",
                  label: "Headquarters",
                  value: "TESCOM Secretariat",
                  sub: "[Confirm building & room, Oke-Mosan, Abeokuta]"
                },
                {
                  icon: "📱",
                  label: "Social Media",
                  value: "[@tescomogunstate]",
                  sub: "Instagram · Facebook · Twitter/X — confirm handles"
                }
              ].map((c) => (
                <div
                  key={c.label}
                  className="bg-white/[0.06] border border-white/10 border-l-4 border-l-[#E8C468] rounded-xl p-5 text-left"
                >
                  <p className="text-xs font-semibold text-white/50 mb-1">
                    {c.icon} {c.label}
                  </p>
                  <p className="font-semibold text-white text-sm">{c.value}</p>
                  <p className="text-xs text-white/40 mt-0.5">{c.sub}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row gap-2 items-center justify-between max-w-3xl mx-auto">
              <p className="text-xs text-white/90">
                © {new Date().getFullYear()} Ogun State Teaching Service Commission. All rights
                reserved.
              </p>
              <p className="text-xs !text-white">
                Built by{" "}
                <a
                  href="https://www.linkedin.com/in/osanyinbi-temitayo-yusuf-b49b22a9/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="!text-[#E8C468] hover:text-white transition-colors font-medium"
                >
                  Teewhy
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* ── BACK TO TOP ───────────────────────────────────────────────── */}
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              onClick={scrollToTop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#0a2e1a] text-[#E8C468] shadow-xl flex items-center justify-center border border-white/10"
              aria-label="Back to top"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>

        {/* ── MAGAZINE MODAL ───────────────────────────────────────────────── */}
        <AnimatePresence>
          {magazineOpen && <MagazineModal issue={issue} onClose={() => setMagazineOpen(false)} />}
        </AnimatePresence>
      </main>
    </>
  );
}

// const HomeViewSection = () => {
//   return (
//     <div className="flex flex-col w-screen overflow-auto bg-black">
//       <div className="flex flex-row gap-10">
//         <LeftContent />
//         <AboutUsHome />
//         <RightContent />
//       </div>

//       <Footer />
//     </div>
//   );
// };

// type Section = {
//   __component: string;
//   // add other fields as needed
// };
// HomeView.tsx
// import { useState } from "react";
// import {
//   HeroSection,
//   AboutSection,
//   // FeaturedNewsSection,
//   // StaffPreviewSection,
//   // MagazineSection,
//   // CallToActionSection,
//   // AnnouncementSection
// } from "components/layouts";

// import { LeftContent, AboutUsHome, RightContent } from "components/layouts/home";

// const HomeView = () => {
//   // const [sections, setSections] = useState<any[]>([]);

//   // useEffect(() => {
//   //   fetch("https://mocki.io/v1/464d372f-2490-46a8-a348-8e2025d40e81")
//   //     .then(async (res) => await res.json())
//   //     .then((data) => {
//   //       console.log("Fetched Sections:", data?.data?.sections);
//   //       setSections(data?.data?.sections || []);
//   //     })
//   //     .catch((err) => console.error(err));
//   // }, []);

//   // if (!sections.length)
//   //   return (
//   //     <div className="min-h-screen flex items-center justify-center text-white">Loading...</div>
//   //   );

//   return (
//     <div className="flex flex-col w-screen overflow-auto bg-black-100">
//       {/* === Always show your original landing page === */}
//       <div className="flex flex-row gap-10">
//         <LeftContent />
//         <AboutUsHome />
//         <RightContent />
//       </div>

//       {/* === Render dynamic sections from API below ===
//       {sections.map((section, index) => {
//         switch (section.__component) {
//           case "sections.hero-section":
//             return <HeroSection key={index} data={section} />;

//           case "sections.about-section":
//             return <AboutSection key={index} data={section} />;

//           // case "sections.featured-news-section":
//           //   return <FeaturedNewsSection key={index} />;

//           // case "sections.staff-preview-section":
//           //   return <StaffPreviewSection key={index} data={section} />;

//           // case "sections.announcement-section":
//           //   return <AnnouncementSection key={index} data={section} />;

//           // case "sections.magazine-section":
//           //   return <MagazineSection key={index} data={section} />;

//           // case "sections.call-to-action-section":
//           //   return <CallToActionSection key={index} data={section} />;

//           default:
//             return null;
//         } */}
//       {/* })} */}
//     </div>
//   );
// };

// export default HomeView;
